import React, { useState, useEffect } from 'react';
import './Settings.css';
import { getTranslation, Language } from '../config/translations';
import LanguageSelect from './LanguageSelect';
import { useLanguage } from '../context/LanguageContext';

interface AppSettings {
  theme: 'ocean' | 'midnight' | 'forest' | 'sunset' | 'nord' | 'dracula';
  notifications: boolean;
  autoSave: boolean;
  language: Language;
  textSize: 'small' | 'medium' | 'large';
  voice: string;
}

interface ThemeConfig {
  name: string;
  id: string;
  primary: string;
  secondary: string;
  background: string;
  text: string;
}

const THEMES: Record<string, ThemeConfig> = {
  ocean: {
    name: 'Ocean',
    id: 'ocean',
    primary: '#00bcd4',
    secondary: '#0097a7',
    background: '#0d1b2a',
    text: '#ffffff',
  },
  midnight: {
    name: 'Midnight',
    id: 'midnight',
    primary: '#6366f1',
    secondary: '#4f46e5',
    background: '#0f172a',
    text: '#f1f5f9',
  },
  forest: {
    name: 'Forest',
    id: 'forest',
    primary: '#10b981',
    secondary: '#059669',
    background: '#0f2818',
    text: '#f0fdf4',
  },
  sunset: {
    name: 'Sunset',
    id: 'sunset',
    primary: '#f97316',
    secondary: '#ea580c',
    background: '#1f1109',
    text: '#fef3c7',
  },
  nord: {
    name: 'Nord',
    id: 'nord',
    primary: '#88c0d0',
    secondary: '#81a1c1',
    background: '#2e3440',
    text: '#eceff4',
  },
  dracula: {
    name: 'Dracula',
    id: 'dracula',
    primary: '#bd93f9',
    secondary: '#ff79c6',
    background: '#282a36',
    text: '#f8f8f2',
  },
};

const Settings: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem('appSettings');
    return saved ? JSON.parse(saved) : {
      theme: 'ocean',
      notifications: true,
      autoSave: true,
      language: 'en' as Language,
      textSize: 'medium',
      voice: 'Emma (F)',
    };
  });

  const t = (key: string) => getTranslation(language, key);

  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  const applyTheme = (themeId: string) => {
    const theme = THEMES[themeId];
    if (!theme) return;

    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.primary);
    root.style.setProperty('--color-secondary', theme.secondary);
    root.style.setProperty('--color-background', theme.background);
    root.style.setProperty('--color-text', theme.text);
  };

  const handleSettingChange = (key: keyof AppSettings, value: any) => {
    const updatedSettings = { ...settings, [key]: value };
    setSettings(updatedSettings);
    localStorage.setItem('appSettings', JSON.stringify(updatedSettings));
    
    // Update language in context if language setting changed
    if (key === 'language') {
      setLanguage(value as Language);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>{t('settings')}</h1>
        <p>{t('managePreferences')}</p>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2>{t('appearance')}</h2>
          <div className="settings-item">
            <div className="settings-label">
              <label>{t('theme')}</label>
              <span className="settings-description">{t('chooseTheme')}</span>
            </div>
          </div>
          <div className="theme-grid">
            {Object.values(THEMES).map((theme) => (
              <button
                key={theme.id}
                className={`theme-card ${settings.theme === theme.id ? 'active' : ''}`}
                onClick={() => handleSettingChange('theme', theme.id)}
                title={t(theme.id)}
              >
                <div
                  className="theme-preview"
                  style={{
                    backgroundColor: theme.background,
                    borderColor: theme.primary,
                  }}
                >
                  <div
                    className="theme-accent"
                    style={{ backgroundColor: theme.primary }}
                  />
                </div>
                <span className="theme-name">{t(theme.id)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="settings-section">
          <h2>{t('displaySettings')}</h2>
          <div className="settings-item">
            <div className="settings-label">
              <label>{t('textSize')}</label>
              <span className="settings-description">{t('chooseTextSize')}</span>
            </div>
            <div className="button-group">
              {(['small', 'medium', 'large'] as const).map((size) => (
                <button
                  key={size}
                  className={`group-btn ${settings.textSize === size ? 'active' : ''}`}
                  onClick={() => handleSettingChange('textSize', size)}
                >
                  {t(size)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h2>{t('voiceSelection')}</h2>
          <div className="settings-item">
            <div className="settings-label">
              <label>{t('preferredVoice')}</label>
              <span className="settings-description">{t('chooseVoice')}</span>
            </div>
          </div>
          <div className="voice-grid">
            {['Emma (F)', 'Sophia (F)', 'Ava (F)', 'Olivia (F)', 'Liam (M)', 'Noah (M)', 'James (M)', 'Elijah (M)'].map((voice) => (
              <button
                key={voice}
                className={`voice-card ${settings.voice === voice ? 'active' : ''}`}
                onClick={() => handleSettingChange('voice', voice)}
              >
                {voice}
              </button>
            ))}
          </div>
        </div>

        <div className="settings-section">
          <h2>{t('notifications')}</h2>
          <div className="settings-item">
            <div className="settings-label">
              <label htmlFor="notifications">{t('enableNotifications')}</label>
              <span className="settings-description">{t('receiveNotifications')}</span>
            </div>
            <input
              id="notifications"
              type="checkbox"
              checked={settings.notifications}
              onChange={(e) => handleSettingChange('notifications', e.target.checked)}
              className="settings-checkbox"
            />
          </div>
        </div>

        <div className="settings-section">
          <h2>{t('general')}</h2>
          <div className="settings-item">
            <div className="settings-label">
              <label htmlFor="autoSave">{t('autoSave')}</label>
              <span className="settings-description">{t('autoSaveDesc')}</span>
            </div>
            <input
              id="autoSave"
              type="checkbox"
              checked={settings.autoSave}
              onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
              className="settings-checkbox"
            />
          </div>

          <div className="settings-item">
            <div className="settings-label">
              <label>{t('language')}</label>
              <span className="settings-description">{t('selectLanguage')}</span>
            </div>
            <LanguageSelect
              value={settings.language}
              onChange={(value) => handleSettingChange('language', value as Language)}
              options={[
                { value: 'en', label: t('english') },
                { value: 'es', label: t('spanish') },
                { value: 'zh', label: t('chinese') },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
